/**
 * Checklist Service
 * 
 * Manages checklist templates and checklist instances via Thalassa API.
 * Updated: 2025-12-17 - Migrated to Thalassa API
 */

import type { ChecklistTemplate, Checklist } from '../types/checklist.types';
import { Finding } from '../../auditcentre';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';
import { cleanDateAndTimeObject } from '@shared/renderDate';

const CHECKLISTS_API = '/v1/checklists';

// Helper to get headers
function getHeaders(tenantId?: string) {
  const companyId = tenantId || window.companyID;
  return {
    'X-Tenancy-Id': companyId,
    'Company-ID': companyId,
  };
}

// Helper function to safely convert timestamps from API response
const convertTimestamps = (data: any) => {
  const result = { ...data };
  
  if (data.createdAt) {
    result.createdAt = typeof data.createdAt === 'string' ? new Date(data.createdAt) : data.createdAt;
  }
  
  if (data.updatedAt) {
    result.updatedAt = typeof data.updatedAt === 'string' ? new Date(data.updatedAt) : data.updatedAt;
  }
  
  if (data.completedAt) {
    result.completedAt = typeof data.completedAt === 'string' ? new Date(data.completedAt) : data.completedAt;
  } else {
    result.completedAt = null;
  }

  if (data.reviewDate) {
    result.reviewDate = cleanDateAndTimeObject(data.reviewDate) ?? null;
  } else if (data.nextReviewDate || data.lastReviewDate) {
    const legacy = data.nextReviewDate ?? data.lastReviewDate;
    result.reviewDate = cleanDateAndTimeObject(legacy) ?? null;
  } else {
    result.reviewDate = null;
  }

  if (data.repeat && typeof data.repeat === 'object') {
    const repeat = { ...data.repeat };
    const start = 'startDate' in repeat ? repeat.startDate : undefined;
    const end = 'endDate' in repeat ? repeat.endDate : undefined;
    result.repeat = {
      ...repeat,
      ...(start !== undefined ? { startDate: cleanDateAndTimeObject(start) ?? null } : {}),
      ...(end !== undefined ? { endDate: cleanDateAndTimeObject(end) ?? null } : {}),
    };

    if (result.repeat && result.repeat.startDate === undefined) {
      result.repeat.startDate = cleanDateAndTimeObject(result.reviewDate ?? null) ?? null;
    }
    if (result.repeat && result.repeat.endDate === undefined) {
      result.repeat.endDate = null;
    }
  }
  
  return result;
};

// Convert API response to ChecklistTemplate
const templateFromAPI = (data: any): ChecklistTemplate => {
  const base = convertTimestamps(data);
  return {
    ...base,
    isReusable: Boolean(data.isReusable),
    isAssuranceTask: Boolean(data.isAssuranceTask),
    createdBy: data.createdBy || '',
    tenantId: data.tenantId || ''
  };
};

// Convert API response to Checklist
const checklistFromAPI = (data: any): Checklist => {
  const base = convertTimestamps(data);
  const normalizedStandards = Array.isArray(data.standards) && data.standards.length
    ? data.standards
    : (data.standard ? [data.standard] : []);
  return {
    ...base,
    status: (data.status as 'draft' | 'in-progress' | 'completed') || 'draft',
    standard: normalizedStandards?.[0] !== undefined ? normalizedStandards[0].replaceAll('Custom', '') : '',
    standards: normalizedStandards?.filter((s: string) => s !== 'Custom'),
    completionHistory: Array.isArray(data.completionHistory)
      ? data.completionHistory.map((c: any) => ({
          ...c,
          completedAt: typeof c.completedAt === 'string' ? new Date(c.completedAt) : c.completedAt,
        }))
      : [],
    templateId: data.templateId || '',
    createdBy: data.createdBy || '',
    tenantId: data.tenantId || '',
    isAssuranceTask: Boolean(data.isAssuranceTask),
    isReusable: typeof data.isReusable === 'boolean' ? data.isReusable : undefined,
    findings: Array.isArray(data.findings) ? data.findings : [],
    repeat: base.repeat,
    reviewDate: base.reviewDate ?? null,
  };
};

export const checklistService = {
  // ==================== TEMPLATES ====================

  async getTemplate(id: string, tenantId: string): Promise<ChecklistTemplate | null> {
    console.log('[ChecklistService] Getting template via Thalassa API:', id);
    try {
      const response = await deiterateAPI.GET(`${CHECKLISTS_API}/templates/${id}`, {
        mode: 'cors',
        backend: 'thalassa',
        headers: getHeaders(tenantId),
      });

      if (response.statusCode === 200 && response.template) {
        return templateFromAPI({ id, docID: id, ...response.template });
      }
      return null;
    } catch (error) {
      console.error('[ChecklistService] Error getting template:', error);
      throw error;
    }
  },

  async getTemplates(tenantId: string): Promise<ChecklistTemplate[]> {
    console.log('[ChecklistService] Getting templates via Thalassa API');
    try {
      const response = await deiterateAPI.GET(`${CHECKLISTS_API}/templates`, {
        mode: 'cors',
        backend: 'thalassa',
        headers: getHeaders(tenantId),
      });

      if (response.statusCode === 200 && response.templates) {
        return response.templates.map((t: any) => ({
          ...templateFromAPI(t),
          isSystem: false
        }));
      }
      return [];
    } catch (error) {
      console.error('[ChecklistService] Error getting templates:', error);
      throw error;
    }
  },

  async saveTemplate(template: Partial<ChecklistTemplate>): Promise<ChecklistTemplate> {
    if (!template.tenantId) {
      throw new Error('Tenant ID is required to save a template');
    }

    const now = new Date();
    const templateStandards = Array.isArray(template.standards) && template.standards.length
      ? template.standards
      : (template.standard ? [template.standard] : []);

    const templateData = {
      title: template.title || '',
      description: template.description || '',
      standard: template.standard || templateStandards[0] || '',
      standards: templateStandards,
      items: template.items || [],
      isReusable: template.isReusable ?? true,
      isAssuranceTask: template.isAssuranceTask ?? false,
      badgeClass: template.badgeClass || '',
      repeat: template.repeat,
      reviewDate: template.reviewDate,
    };

    console.log('[ChecklistService] Saving template via Thalassa API');

    if (template.id) {
      // Update existing template
      const response = await deiterateAPI.PATCH(`${CHECKLISTS_API}/templates/${template.id}`, {
        mode: 'cors',
        backend: 'thalassa',
        headers: getHeaders(template.tenantId),
        body: JSON.stringify(templateData),
      });

      if (response.statusCode === 200 && response.template) {
        return templateFromAPI(response.template);
      }
      throw new Error(response.error || 'Failed to update template');
    } else {
      // Create new template
      const response = await deiterateAPI.POST(`${CHECKLISTS_API}/templates`, {
        mode: 'cors',
        backend: 'thalassa',
        headers: getHeaders(template.tenantId),
        body: JSON.stringify(templateData),
      });

      if (response.statusCode === 201 && response.template) {
        return templateFromAPI(response.template);
      }
      throw new Error(response.error || 'Failed to create template');
    }
  },

  async deleteTemplate(id: string, tenantId: string): Promise<void> {
    console.log('[ChecklistService] Deleting template via Thalassa API:', id);
    const response = await deiterateAPI.DELETE(`${CHECKLISTS_API}/templates/${id}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: getHeaders(tenantId),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to delete template');
    }
  },

  // ==================== CHECKLISTS ====================

  async createChecklist(checklist: Omit<Checklist, 'id'> & { id?: string }, tenantId: string): Promise<Checklist> {
    console.log('[ChecklistService] Creating checklist via Thalassa API');
    
    const standards = Array.isArray(checklist.standards) && checklist.standards.length
      ? checklist.standards
      : (checklist.standard ? [checklist.standard] : []);

    const checklistData = {
      title: checklist.title,
      description: checklist.description,
      templateId: checklist.templateId,
      standard: standards[0] ?? '',
      standards,
      items: checklist.items,
      isAssuranceTask: checklist.isAssuranceTask,
      repeat: checklist.repeat,
      reviewDate: checklist.reviewDate,
    };

    console.log('[ChecklistService] Sending checklist data to API:', JSON.stringify(checklistData, null, 2));
    console.log('[ChecklistService] Items being sent:', checklist.items);

    const response = await deiterateAPI.POST(CHECKLISTS_API, {
      mode: 'cors',
      backend: 'thalassa',
      headers: getHeaders(tenantId),
      body: JSON.stringify(checklistData),
    });

    if (response.statusCode === 201 && response.checklist) {
      return checklistFromAPI(response.checklist);
    }
    throw new Error(response.error || 'Failed to create checklist');
  },

  async createChecklistFromTemplate(
    templateId: string, 
    tenantId: string, 
    createdBy: string
  ): Promise<Checklist> {
    console.log('[ChecklistService] Creating checklist from template via Thalassa API:', templateId);
    
    const response = await deiterateAPI.POST(`${CHECKLISTS_API}/from-template/${templateId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        ...getHeaders(tenantId),
        'X-User-Id': createdBy,
      },
    });

    if (response.statusCode === 201 && response.checklist) {
      return checklistFromAPI(response.checklist);
    }
    throw new Error(response.error || 'Failed to create checklist from template');
  },

  async getChecklist(id: string, tenantId: string): Promise<Checklist | null> {
    console.log('[ChecklistService] Getting checklist via Thalassa API:', id);
    try {
      const response = await deiterateAPI.GET(`${CHECKLISTS_API}/${id}`, {
        mode: 'cors',
        backend: 'thalassa',
        headers: getHeaders(tenantId),
      });

      if (response.statusCode === 200 && response.checklist) {
        return checklistFromAPI(response.checklist);
      }
      return null;
    } catch (error) {
      console.error('[ChecklistService] Error getting checklist:', error);
      throw error;
    }
  },

  async getChecklists(tenantId: string, templateId?: string): Promise<Checklist[]> {
    console.log('[ChecklistService] Getting checklists via Thalassa API');
    try {
      const params = new URLSearchParams();
      if (templateId) params.set('templateId', templateId);
      
      const query = params.toString();
      const url = query ? `${CHECKLISTS_API}?${query}` : CHECKLISTS_API;

      const response = await deiterateAPI.GET(url, {
        mode: 'cors',
        backend: 'thalassa',
        headers: getHeaders(tenantId),
      });

      if (response.statusCode === 200 && response.checklists) {
        return response.checklists.map((c: any) => checklistFromAPI(c));
      }
      return [];
    } catch (error) {
      console.error('[ChecklistService] Error getting checklists:', error);
      throw error;
    }
  },

  async updateChecklist(
    id: string, 
    updates: Partial<Checklist>,
    tenantId: string
  ): Promise<void> {
    console.log('[ChecklistService] Updating checklist via Thalassa API:', id);
    try {
      const response = await deiterateAPI.PATCH(`${CHECKLISTS_API}/${id}`, {
        mode: 'cors',
        backend: 'thalassa',
        headers: getHeaders(tenantId),
        body: JSON.stringify(updates),
      });

      if (response.statusCode !== 200) {
        throw new Error(response.error || 'Failed to update checklist');
      }
    } catch (error) {
      console.error('[ChecklistService] Error updating checklist:', error);
      throw error;
    }
  },

  async deleteChecklist(id: string, tenantId: string): Promise<void> {
    console.log('[ChecklistService] Deleting checklist via Thalassa API:', id);
    try {
      const response = await deiterateAPI.DELETE(`${CHECKLISTS_API}/${id}`, {
        mode: 'cors',
        backend: 'thalassa',
        headers: getHeaders(tenantId),
      });

      if (response.statusCode !== 200) {
        throw new Error(response.error || 'Failed to delete checklist');
      }
    } catch (error) {
      console.error('[ChecklistService] Error deleting checklist:', error);
      throw error;
    }
  },

  async submitChecklist(id: string, userId: string, tenantId: string, signature?: string, values?: Record<string, {title: string, value: any}>, findings?: Finding[]): Promise<void> {
    console.log('[ChecklistService] Submitting checklist via Thalassa API:', id);
    try {
      const response = await deiterateAPI.POST(`${CHECKLISTS_API}/${id}/submit`, {
        mode: 'cors',
        backend: 'thalassa',
        headers: {
          ...getHeaders(tenantId),
          'X-User-Id': userId,
        },
        body: JSON.stringify({
          signature,
          values,
          findings: findings || [],
        }),
      });

      if (response.statusCode !== 200) {
        throw new Error(response.error || 'Failed to submit checklist');
      }
    } catch (error) {
      console.error('[ChecklistService] Error submitting checklist:', error);
      throw error;
    }
  }
};
